/**
 * Shared React Query keys so catalogs coalesce across pages
 * (Services ↔ Requisitions ↔ Sales ↔ Customers).
 */

export const queryKeys = {
  permissionsMe: (userId) => ['permissions', 'me', userId ?? 'anon'],
  permissionsCatalog: 'permissions',

  serviceCustomers: ['service-customers'],
  serviceCustomersSearch: (search) => ['service-customers', search || ''],
  servicePackages: ['service-packages'],
  serviceCompanies: 'service-companies',
  serviceEnrollments: 'service-enrollments',
  serviceActivities: 'service-activities',
  serviceCandidates: (role) => ['service-candidates', role],
  serviceConsultants: ['users', 'picker', 'consultant'],

  users: 'users',
  usersPicker: (scope) => ['users', 'picker', scope || 'all'],

  requisitionWorkflow: ['requisition-workflow'],
  buyingWorkflow: ['buying-workflow'],

  notificationsSummary: ['notifications', 'summary'],
  notificationsList: ['notifications', 'list'],

  /** Jobs list — include page/limit/filters so pages don't share stale dumps. */
  jobs: (filters = {}) => [
    'jobs',
    {
      search: filters.search || '',
      location: filters.location || '',
      employmentType: filters.employmentType || '',
      listingType: filters.listingType || '',
      status: filters.status || '',
      jobViewMode: filters.jobViewMode || 'matched',
      role: filters.role || '',
      page: filters.page ?? 1,
      limit: filters.limit ?? 25,
      includeDeleted: filters.includeDeleted || false,
    },
  ],
  jobsRoot: 'jobs',
  job: (id) => ['job', String(id)],
  jobsPicker: (limit = 100) => ['jobs', 'picker', { limit }],

  crm: (filters = {}) => [
    'crm',
    {
      page: filters.page ?? 1,
      limit: filters.limit ?? 25,
      status: filters.status || '',
      type: filters.type || '',
      search: filters.search || '',
      candidateId: filters.candidateId || '',
      sort: filters.sort || 'newest',
    },
  ],
  crmRoot: 'crm',
  crmDetail: (id) => ['crm', 'detail', String(id)],
  crmCandidates: (role) => ['crm-candidates', role],

  timesheets: (filters = {}) => [
    'timesheets',
    {
      page: filters.page ?? 1,
      limit: filters.limit ?? 25,
      status: filters.status || '',
      search: filters.search || '',
    },
  ],
  timesheetsRoot: 'timesheets',
  timesheetDetail: (id) => ['timesheet', String(id)],
  timesheetCandidates: (role) => ['timesheet-candidates', role],

  financeSettings: ['service-finance-settings'],
  financePayRun: ({ periodStart, periodEnd, q } = {}) => [
    'service-finance-pay-run',
    { periodStart: periodStart || '', periodEnd: periodEnd || '', q: q || '' },
  ],
  financeLedger: ({ periodStart, periodEnd, consultantId } = {}) => [
    'service-finance-ledger',
    {
      periodStart: periodStart || '',
      periodEnd: periodEnd || '',
      consultantId: consultantId || '',
    },
  ],
  financePayoutInvoices: (filters = {}) => [
    'service-finance-payout-invoices',
    { q: filters.q || '', limit: filters.limit ?? 100 },
  ],

  salesDocuments: (filterType = '') => ['service-sales-documents', filterType || ''],

  clientOrders: (filters = {}) => ['client-orders', filters],
  clientOrder: (id) => ['client-orders', 'detail', String(id)],
  sellingOrders: (filters = {}) => ['selling', 'orders', filters],
  creditNotes: (filters = {}) => ['selling', 'credit-notes', filters],
  vendors: ['vendors'],
  vendorPos: (filters = {}) => ['buying', 'vendor-pos', filters],
  vendorBills: (filters = {}) => ['buying', 'vendor-bills', filters],
  vendorQuotations: (filters = {}) => ['buying', 'vendor-quotations', filters],
  buyingOcrStatus: ['buying-ocr-status'],
  accountingAccounts: ['accounting', 'accounts'],
  accountingJournals: ['accounting', 'journals'],
  accountingPayments: ['accounting', 'payments'],
  accountingCompanies: ['accounting', 'companies'],
  companyMappings: ['accounting', 'company-mappings'],
  crmPipelineLeads: ['crm-pipeline', 'leads'],
  crmPipelineOpportunities: ['crm-pipeline', 'opportunities'],
  erpProjects: ['erp-projects'],
  erpProject: (id) => ['erp-projects', 'detail', String(id)],

  supportStats: ['support', 'stats'],
  supportAgents: ['support', 'agents'],
  supportTickets: (filters = {}) => [
    'support',
    'tickets',
    {
      status: filters.status || '',
      priority: filters.priority || '',
      assignee_id: filters.assignee_id || '',
      search: filters.search || '',
      page: filters.page ?? 1,
      limit: filters.limit ?? 25,
    },
  ],
  supportTicket: (id) => ['support', 'ticket', String(id)],
};

export function invalidateSellingOrders(queryClient) {
  queryClient.invalidateQueries(queryKeys.sellingOrders());
  queryClient.invalidateQueries('erp-so');
}

export function invalidateCreditNotes(queryClient) {
  queryClient.invalidateQueries(queryKeys.creditNotes());
  queryClient.invalidateQueries('erp-cn');
}

export function invalidateProjects(queryClient) {
  queryClient.invalidateQueries(queryKeys.erpProjects);
  queryClient.invalidateQueries('erp-projects');
}

export function invalidatePayments(queryClient) {
  queryClient.invalidateQueries(queryKeys.accountingPayments);
  queryClient.invalidateQueries('erp-payments');
  queryClient.invalidateQueries(['erp-report']);
}

/** Freshness tiers — keep UI interactive; background-revalidate when stale. */
export const staleTimes = {
  /** Default for list/detail queries */
  lists: 30_000,
  /** Roles, packages, workflow, currencies, permission maps */
  reference: 5 * 60_000,
  /** Session grants (/permissions/me) */
  session: 2 * 60_000,
  /** Notification badge poll interval companion */
  notifications: 60_000,
};
